import { Interface } from 'ethers'
import type { Hex } from './types.js'
import { getPrivacy, maskAddress } from './lib/privacy.js'

/**
 * Best-effort decoder for Module Safe tx payloads. Knows about the common
 * patterns: execTransactionFromModule, ERC20 transfer/approve, and unwraps
 * one level so the UI can show the inner call target + selector.
 */

const EXEC_FROM_MODULE = new Interface([
  'function execTransactionFromModule(address to, uint256 value, bytes data, uint8 operation) returns (bool)',
])

const ERC20 = new Interface([
  'function transfer(address to, uint256 amount) returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) returns (bool)',
  'function approve(address spender, uint256 amount) returns (bool)',
])

const ERC721 = new Interface([
  'function setApprovalForAll(address operator, bool approved)',
  'function safeTransferFrom(address from, address to, uint256 tokenId)',
  'function transferFrom(address from, address to, uint256 tokenId)',
])

export interface Decoded {
  summary: string
  inner?: {
    to: string
    value: string
    data: Hex
    operation: number
    summary: string
  }
}

const KNOWN_SELECTORS: Record<string, string> = {
  '0x468721a7': 'execTransactionFromModule',
  '0xa9059cbb': 'transfer',
  '0x23b872dd': 'transferFrom',
  '0x095ea7b3': 'approve',
  '0xa22cb465': 'setApprovalForAll',
  '0x42842e0e': 'safeTransferFrom',
  '0x4630a0d8': 'LiFi.swapTokensGeneric',
  '0x4666fc80': 'LiFi.swapTokensSingleV3ERC20ToERC20',
  '0xaf7060fd': 'LiFi.swapTokensSingleV3NativeToERC20',
  '0x733214a3': 'LiFi.swapTokensSingleV3ERC20ToNative',
  '0x6a761202': 'Safe.execTransaction',
}

export function decodeCalldata(to: string, value: string, data: Hex): Decoded {
  if (!data || data === '0x' || data.length < 10) {
    return { summary: `call ${short(to)} ${formatValue(value)}` }
  }

  const selector = data.slice(0, 10).toLowerCase()

  if (selector === '0x468721a7') {
    try {
      const parsed = EXEC_FROM_MODULE.decodeFunctionData(
        'execTransactionFromModule',
        data,
      )
      const innerTo = parsed[0] as string
      const innerValue = (parsed[1] as bigint).toString()
      const innerData = parsed[2] as Hex
      const innerOp = Number(parsed[3] as bigint)
      const innerDecoded = decodeCalldata(innerTo, innerValue, innerData)
      return {
        summary: `execTransactionFromModule → ${innerDecoded.summary}`,
        inner: {
          to: innerTo,
          value: innerValue,
          data: innerData,
          operation: innerOp,
          summary: innerDecoded.summary,
        },
      }
    } catch {
      // fall through
    }
  }

  const named = KNOWN_SELECTORS[selector]
  if (named === 'transfer') {
    try {
      const args = ERC20.decodeFunctionData('transfer', data)
      return {
        summary: `ERC20 transfer → ${short(args[0] as string)} (${(args[1] as bigint).toString()} units)`,
      }
    } catch {
      /* fallthrough */
    }
  }
  if (named === 'approve') {
    try {
      const args = ERC20.decodeFunctionData('approve', data)
      const amount = args[1] as bigint
      const isMax = amount === 2n ** 256n - 1n
      return {
        summary: `ERC20 approve → ${short(args[0] as string)} ${isMax ? '(unlimited)' : `(${amount.toString()})`}`,
      }
    } catch {
      /* fallthrough */
    }
  }
  if (named === 'setApprovalForAll') {
    try {
      const args = ERC721.decodeFunctionData('setApprovalForAll', data)
      return {
        summary: `ERC721 setApprovalForAll → ${short(args[0] as string)} (${args[1] ? 'TRUE' : 'false'})`,
      }
    } catch {
      /* fallthrough */
    }
  }

  return {
    summary: `${named ?? selector} → ${short(to)} ${formatValue(value)}`,
  }
}

export function short(addr: string): string {
  if (!addr || addr.length < 10) return addr
  if (getPrivacy()) return maskAddress(addr)
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`
}

function formatValue(value: string): string {
  if (value === '0' || value === '') return ''
  try {
    return ` (value ${BigInt(value).toString()} wei)`
  } catch {
    return ''
  }
}
